import React, { useState } from 'react';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';

import { getBadgeImage } from 'images/RelicVault/badges';
import { BadgeShieldIcon, LockIcon } from '../shared/RelicVaultIcons';
import { hoverTransition } from '../shared/theme';
import { tierByKey } from 'utils/relicVault/relicDisplay';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

/**
 * One badge in the grid. The badge name is part of the artwork, so the tile is
 * art only - the name and how to earn it live in the detail popup.
 *
 * Three states: earned (full colour), locked (greyed, lock overlay), and
 * not-yet-designed (no artwork yet, so a neutral plate stands in for it).
 */
const BadgeCard = ({ badge, owned, size, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const image = getBadgeImage(badge.id);
  const rarityColor = tierByKey(badge.rarity)?.color ?? '#4ADE80';

  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={() => onSelect(badge.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={owned ? badge.name : `${badge.name}, locked`}
      style={[
        styles.card,
        { width: size, height: size },
        hovered && styles.cardHover,
        hoverTransition(120),
      ]}>
      <View
        style={[
          StyleSheet.absoluteFill,
          styles.glow,
          owned && image
            ? IS_WEB
              ? { backgroundImage: iconGlowFor(rarityColor) }
              : { backgroundColor: `${rarityColor}22` }
            : styles.glowLocked,
        ]}
      />

      {image ? (
        <Image
          source={image}
          resizeMode="contain"
          style={[
            styles.art,
            // Locked badges keep their shape but lose their colour. Native has
            // no greyscale filter and tinting would flatten the whole badge to
            // one colour, so there it just dims instead.
            owned
              ? webOnly({ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))' })
              : IS_WEB
              ? { filter: 'grayscale(1) brightness(0.55)', opacity: 0.85 }
              : { opacity: 0.32 },
          ]}
        />
      ) : (
        <View style={styles.placeholder}>
          <BadgeShieldIcon size={Math.round(size * 0.26)} color="rgba(201,193,240,0.45)" />
        </View>
      )}

      {!owned && !!image && (
        <View style={styles.lock}>
          <LockIcon size={Math.round(size * 0.16)} strokeWidth="2.4" />
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardHover: {
    transform: [{ translateY: -2 }],
  },
  glow: {
    borderRadius: 999,
  },
  glowLocked: {
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  art: {
    width: '100%',
    height: '100%',
  },
  // Shown until a badge has artwork: a plate so the grid keeps its shape and
  // the tile still reads as a badge slot rather than an empty cell.
  placeholder: {
    width: '76%',
    height: '76%',
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: 'rgba(255,255,255,0.14)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lock: {
    position: 'absolute',
    width: '30%',
    aspectRatio: 1,
    borderRadius: 999,
    backgroundColor: 'rgba(6,5,18,0.9)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default React.memo(BadgeCard);
